const DEV_MODE = process.env.NODE_ENV !== "production";
const REDACTED = "[redacted]";
const REDACTED_METADATA = "[redacted-metadata]";
const SENSITIVE_KEY_PATTERN = /(api[_-]?key|apikey|authorization|auth|token|secret|password|passphrase|private[_-]?key|jwt|bearer)/i;
const METADATA_KEY_PATTERN = /(metadata|metadatajson|metadata_json)/i;
const MAX_OBJECT_KEYS = 20;

function clampNumber(value, fallback, min = 0, max = Number.POSITIVE_INFINITY) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
}

const OBS_ENABLED = process.env.REACT_APP_MARKETPLACE_OBSERVABILITY_ENABLED === "true";
const OBS_CONSOLE = process.env.REACT_APP_MARKETPLACE_OBSERVABILITY_CONSOLE === "true";
const OBS_SAMPLE_RATE = clampNumber(process.env.REACT_APP_MARKETPLACE_OBSERVABILITY_SAMPLE_RATE, 1, 0, 1);
const MIN_SUMMARY_INTERVAL_MS = DEV_MODE ? 1000 : 30000;
const OBS_SUMMARY_INTERVAL_MS = Math.max(
  MIN_SUMMARY_INTERVAL_MS,
  Math.floor(
    clampNumber(
      process.env.REACT_APP_MARKETPLACE_OBSERVABILITY_SUMMARY_INTERVAL_MS,
      30000,
      MIN_SUMMARY_INTERVAL_MS
    )
  )
);
const OBS_VERBOSE = DEV_MODE && process.env.REACT_APP_MARKETPLACE_DEBUG === "1";

const MAX_EVENT_BUFFER = 200;
const MAX_ACTIVE_TIMERS = 1000;

const counters = new Map();
const metrics = new Map();
const gauges = new Map();
const activeTimers = new Map();
const eventBuffer = [];

let summaryBuilder = null;
let summaryTimer = null;
let lastFlushAt = 0;

function nowMs() {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}

function toSafeString(value, maxLength = 120) {
  const text = String(value ?? "");
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
}

function safeRun(fn, fallback = null) {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

function isSensitiveKey(key) {
  return SENSITIVE_KEY_PATTERN.test(String(key || ""));
}

function isMetadataKey(key) {
  return METADATA_KEY_PATTERN.test(String(key || ""));
}

function stripSensitiveUrl(url) {
  if (!url) return null;
  try {
    const parsed = new URL(String(url));
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    const text = String(url);
    const q = text.indexOf("?");
    const h = text.indexOf("#");
    const cut = [q, h].filter((idx) => idx >= 0).sort((a, b) => a - b)[0];
    return cut == null ? text : text.slice(0, cut);
  }
}

function sanitizeValue(value, depth = 0, seen = new WeakSet()) {
  if (value == null) return value;
  if (depth > 2) return "[depth-limited]";

  if (typeof value === "string") {
    if (value.startsWith("http://") || value.startsWith("https://")) {
      return stripSensitiveUrl(value);
    }
    return toSafeString(value);
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (typeof value === "bigint") {
    return value.toString();
  }

  if (Array.isArray(value)) {
    return value.slice(0, 20).map((entry) => sanitizeValue(entry, depth + 1, seen));
  }

  if (typeof value === "object") {
    if (seen.has(value)) return "[circular]";
    seen.add(value);

    const out = {};
    const entries = Object.entries(value);
    for (const [index, [key, entry]] of entries.entries()) {
      if (index >= MAX_OBJECT_KEYS) {
        out.__truncated__ = true;
        break;
      }

      if (isSensitiveKey(key)) {
        out[key] = REDACTED;
        continue;
      }

      if (isMetadataKey(key)) {
        out[key] = REDACTED_METADATA;
        continue;
      }

      out[key] = sanitizeValue(entry, depth + 1, seen);
    }
    return out;
  }

  return toSafeString(value);
}

function sanitizeTags(tags) {
  if (!tags || typeof tags !== "object") return null;
  const out = {};
  for (const [key, value] of Object.entries(tags)) {
    if (isSensitiveKey(key)) {
      out[key] = REDACTED;
      continue;
    }
    if (isMetadataKey(key)) {
      out[key] = REDACTED_METADATA;
      continue;
    }
    out[key] = sanitizeValue(value);
  }
  return out;
}

function shouldCollect() {
  if (!OBS_ENABLED) return false;
  if (OBS_SAMPLE_RATE >= 1) return true;
  return Math.random() < OBS_SAMPLE_RATE;
}

function ensureSummaryTimer() {
  if (!OBS_ENABLED || !OBS_CONSOLE || summaryTimer) return;
  if (typeof window === "undefined") return;

  summaryTimer = window.setInterval(() => {
    flushMarketplaceMetrics();
  }, OBS_SUMMARY_INTERVAL_MS);
}

function clearSummaryTimerIfIdle() {
  if (!summaryBuilder && summaryTimer) {
    if (typeof window !== "undefined") {
      window.clearInterval(summaryTimer);
    }
    summaryTimer = null;
  }
}

export function isMarketplaceObservabilityEnabled() {
  return OBS_ENABLED;
}

export function getMarketplaceObservabilityConfig() {
  return {
    enabled: OBS_ENABLED,
    console: OBS_CONSOLE,
    sampleRate: OBS_SAMPLE_RATE,
    summaryIntervalMs: OBS_SUMMARY_INTERVAL_MS,
    verbose: OBS_VERBOSE,
  };
}

export function recordMetric(name, value, tags = null) {
  safeRun(() => {
    if (!shouldCollect()) return;
    const metricName = String(name || "metric.unknown");
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return;

    const next = metrics.get(metricName) || {
      count: 0,
      sum: 0,
      min: numericValue,
      max: numericValue,
      last: numericValue,
      lastTags: null,
    };

    next.count += 1;
    next.sum += numericValue;
    next.min = Math.min(next.min, numericValue);
    next.max = Math.max(next.max, numericValue);
    next.last = numericValue;
    next.lastTags = sanitizeTags(tags);
    metrics.set(metricName, next);
  });
}

export function incrementCounter(name, tags = null, delta = 1) {
  safeRun(() => {
    if (!shouldCollect()) return;
    const counterName = String(name || "counter.unknown");
    const inc = Number.isFinite(Number(delta)) ? Number(delta) : 1;

    const next = counters.get(counterName) || {
      value: 0,
      lastTags: null,
    };

    next.value += inc;
    next.lastTags = sanitizeTags(tags);
    counters.set(counterName, next);
  });
}

export function setGauge(name, value, tags = null) {
  safeRun(() => {
    if (!shouldCollect()) return;
    const gaugeName = String(name || "gauge.unknown");
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return;
    gauges.set(gaugeName, {
      value: numericValue,
      lastTags: sanitizeTags(tags),
    });
  });
}

export function startTimer(name, tags = null) {
  return safeRun(() => {
    if (!shouldCollect()) return null;
    if (activeTimers.size > MAX_ACTIVE_TIMERS) {
      const oldestKey = activeTimers.keys().next().value;
      if (oldestKey) activeTimers.delete(oldestKey);
    }

    const id = `${String(name || "timer.unknown")}:${Date.now()}:${Math.random().toString(16).slice(2)}`;
    activeTimers.set(id, {
      metricName: String(name || "timer.unknown"),
      startedAt: nowMs(),
      tags: sanitizeTags(tags),
    });
    return id;
  }, null);
}

export function endTimer(timerId, extraTags = null) {
  return safeRun(() => {
    if (!timerId || !activeTimers.has(timerId)) return null;
    const timer = activeTimers.get(timerId);
    activeTimers.delete(timerId);

    const durationMs = Math.max(0, nowMs() - Number(timer.startedAt || nowMs()));
    recordMetric(timer.metricName, durationMs, {
      ...(timer.tags || {}),
      ...(sanitizeTags(extraTags) || {}),
    });
    return durationMs;
  }, null);
}

export function recordEvent(name, payload = null) {
  safeRun(() => {
    if (!shouldCollect()) return;
    const entry = {
      ts: Date.now(),
      name: String(name || "event.unknown"),
      payload: sanitizeValue(payload),
    };

    eventBuffer.push(entry);
    if (eventBuffer.length > MAX_EVENT_BUFFER) {
      eventBuffer.splice(0, eventBuffer.length - MAX_EVENT_BUFFER);
    }

    if (OBS_VERBOSE && OBS_CONSOLE) {
      console.log(`[marketplace-observability] event ${entry.name}`, entry.payload || {});
    }
  });
}

export function setMarketplaceSummaryBuilder(builder) {
  safeRun(() => {
    if (typeof builder !== "function") return;
    summaryBuilder = builder;
    ensureSummaryTimer();
  });
}

export function clearMarketplaceSummaryBuilder() {
  safeRun(() => {
    summaryBuilder = null;
    clearSummaryTimerIfIdle();
  });
}

function metricSnapshot(map) {
  const out = {};
  for (const [key, value] of map.entries()) {
    out[key] = value;
  }
  return out;
}

export function getMarketplaceMetricsSnapshot() {
  return safeRun(
    () => ({
      config: getMarketplaceObservabilityConfig(),
      counters: metricSnapshot(counters),
      metrics: metricSnapshot(metrics),
      gauges: metricSnapshot(gauges),
      events: [...eventBuffer],
      activeTimers: activeTimers.size,
      lastFlushAt,
    }),
    {
      config: getMarketplaceObservabilityConfig(),
      counters: {},
      metrics: {},
      gauges: {},
      events: [],
      activeTimers: 0,
      lastFlushAt,
    }
  );
}

export function flushMarketplaceMetrics(options = {}) {
  return safeRun(() => {
    if (!OBS_ENABLED || !OBS_CONSOLE) return null;
    const force = Boolean(options.force);
    const now = Date.now();
    if (!force && now - lastFlushAt < OBS_SUMMARY_INTERVAL_MS) return null;

    lastFlushAt = now;
    const snapshot = getMarketplaceMetricsSnapshot();
    const summary = safeRun(
      () => (typeof summaryBuilder === "function" ? summaryBuilder(snapshot) : null),
      null
    );

    console.groupCollapsed("[marketplace-observability] summary");
    if (summary && Array.isArray(summary.lines)) {
      summary.lines.forEach((line) => {
        console.log(line);
      });
    } else if (summary && typeof summary === "object") {
      for (const [key, value] of Object.entries(summary)) {
        console.log(`${key}:`, value);
      }
    } else {
      console.log("No summary builder attached.");
    }
    if (OBS_VERBOSE) {
      console.log("snapshot", snapshot);
    }
    console.groupEnd();
    return snapshot;
  }, null);
}

safeRun(() => {
  if (OBS_ENABLED && typeof window !== "undefined") {
    window.__MARKETPLACE_METRICS__ = getMarketplaceMetricsSnapshot;
    if (OBS_VERBOSE) {
      window.__MARKETPLACE_METRICS_FLUSH__ = () => flushMarketplaceMetrics({ force: true });
    }
  }
});
